/**
 * De onde vem um vídeo, a partir da URL.
 *
 * Existe porque a mesma expressão estava escrita em três lugares (o popup do
 * vídeo do exercício, o exercício aberto e o construtor de página), cada uma com
 * uma variação: uma pegava `shorts/`, outra não, e a terceira não reconhecia
 * `live/`. Colar o mesmo link em duas telas dava resultado diferente.
 */

export type OrigemDoVideo =
  | { tipo: "youtube"; id: string }
  | { tipo: "vimeo"; id: string }
  | { tipo: "arquivo" }
  | { tipo: "nenhum" }

export function origemDoVideo(url?: string | null): OrigemDoVideo {
  const limpa = (url ?? "").trim()
  if (!limpa) return { tipo: "nenhum" }

  const yt = limpa.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  )
  if (yt) return { tipo: "youtube", id: yt[1] }

  const vimeo = limpa.match(/vimeo\.com\/(?:video\/)?(\d+)/)
  if (vimeo) return { tipo: "vimeo", id: vimeo[1] }

  /* Qualquer outra coisa com cara de endereço é tratada como arquivo direto: é o
     caso do que sobe para o nosso S3. */
  if (/^https?:\/\//i.test(limpa)) return { tipo: "arquivo" }

  return { tipo: "nenhum" }
}

/** Miniatura do vídeo, quando o serviço entrega uma sem precisar de chamada. */
export function miniaturaDoVideo(url?: string | null): string | null {
  const origem = origemDoVideo(url)
  return origem.tipo === "youtube"
    ? `https://img.youtube.com/vi/${origem.id}/mqdefault.jpg`
    : null
}

/** Endereço para tocar dentro do app. `null` quando é arquivo direto (usa `<video>`). */
export function embedDoVideo(url?: string | null): string | null {
  const origem = origemDoVideo(url)
  if (origem.tipo === "youtube") return `https://www.youtube.com/embed/${origem.id}`
  if (origem.tipo === "vimeo") return `https://player.vimeo.com/video/${origem.id}`
  return null
}

/** O nome do lugar, para escrever na tela: "YouTube", "Vimeo", "arquivo enviado". */
export function nomeDaOrigem(url?: string | null): string | null {
  switch (origemDoVideo(url).tipo) {
    case "youtube":
      return "YouTube"
    case "vimeo":
      return "Vimeo"
    case "arquivo":
      return "arquivo enviado"
    default:
      return null
  }
}
